import type { ModuleDefinition } from "../registry";
import styles from "./ModuleTile.module.css";

export type ModuleBadgeTone = "teal" | "red" | "amber";

export interface ModuleBadge {
  count: number;
  tone: ModuleBadgeTone;
}

interface ModuleTileProps {
  readonly module: ModuleDefinition;
  readonly isPinned: boolean;
  readonly onTogglePin: () => void;
  readonly onOpen: () => void;
  readonly badge?: ModuleBadge;
  /** Backend lists this module but the credentials endpoint returned nothing. */
  readonly setupRequired?: boolean;
}

const TONE_CLASSES: Record<ModuleBadgeTone, string> = {
  teal: styles.badgeTeal!,
  red: styles.badgeRed!,
  amber: styles.badgeAmber!,
};

function accentClassFor(id: string): string {
  return styles[`accent_${id}`] ?? "";
}

export function ModuleTile({
  module,
  isPinned,
  onTogglePin,
  onOpen,
  badge,
  setupRequired = false,
}: Readonly<ModuleTileProps>) {
  const Icon = module.icon;

  const handlePinClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onTogglePin();
  };

  const tileClasses = [
    styles.tile,
    accentClassFor(module.id),
    setupRequired ? styles.tileSetupRequired : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={`${styles.tileWrap} ${isPinned ? styles.tilePinned : ""}`}>
      <button
        type="button"
        className={tileClasses}
        onClick={onOpen}
        aria-describedby={setupRequired ? `${module.id}-setup-msg` : undefined}
      >
        <div className={styles.iconSquare}>
          <span className={styles.iconInner}>
            <Icon />
          </span>
        </div>
        <span className={styles.tileName}>{module.name}</span>
        <span className={styles.tileSub}>
          {setupRequired ? "Setup required" : module.subtitle}
        </span>
        {setupRequired && (
          <span
            id={`${module.id}-setup-msg`}
            className={styles.setupPill}
            aria-label="Credentials not configured"
          >
            !
          </span>
        )}
        {!setupRequired && badge && (
          <span
            className={`${styles.badge} ${TONE_CLASSES[badge.tone]}`}
            aria-label={`${badge.count} notifications`}
          >
            {badge.count}
          </span>
        )}
      </button>
      <button
        type="button"
        className={`${styles.pinBtn} ${isPinned ? styles.pinBtnActive : ""}`}
        onClick={handlePinClick}
        title={isPinned ? "Unpin from tabs" : "Pin to tabs"}
        aria-label={isPinned ? `Unpin ${module.name} from tabs` : `Pin ${module.name} to tabs`}
      >
        <span className={styles.pinEmoji} aria-hidden="true">📌</span>
      </button>
    </div>
  );
}
